// Report filter configurations - defines which filters are used by each report type

/**
 * PD-16919 — every list with a Salespeople filter also offers Teams, so a
 * manager can read a whole team's numbers at once. The server turns the
 * picked teams into their members (ReportingService.enforceSalespersonFilter),
 * so a report needs no team-specific SQL; it only has to send `teams` along
 * with the rest of its filters.
 */
export const REPORT_FILTER_CONFIGS = {
	salesAnalysis: [
		'dateRange',
		'customers',
		'salespeople',
		'teams',
		'businessTypes',
		'groups',
		'items',
		'productCategories',
		'companies',
	],

	companyBreakdownSales: [
		'dateRange',
		'customers',
		'salespeople',
		'teams',
		'businessTypes',
		'groups',
		'items',
		'productCategories',
		'companies',
	],

	customerAnalytics: [
		'dateRange',
		'customers',
		'leadOrCustomer',
		'groups',
		'businessTypes',
		'items',
		'productCategories',
		'companies',
	],

	inventoryReports: [
		'dateRange',
		'items',
		'productCategories',
		'warehouses',
		'companies',
	],

	territoryAnalysis: [
		'dateRange',
		'customers',
		'salespeople',
		'teams',
		'companies',
	],

	activeOrders: [
		'dateRange',
		'customers',
		'salespeople',
		'teams',
		'businessTypes',
		'groups',
		'items',
		'productCategories',
		'companies',
	],

	clientPaymentStatus: [
		'dateRange',
		'customers',
		'salespeople',
		'teams',
		'businessTypes',
		'groups',
		'items',
		'productCategories',
		'companies',
	],

	kpiOverview: [
		'dateRange',
		'customers',
		'salespeople',
		'businessTypes',
		'groups',
		'items',
		'productCategories',
		'teams',
		'companies',
	],

	salesPipeline: [
		'dateRange',
		'opportunityStages',
		'salespeople',
		'teams',
		'businessTypes',
		'companies',
	],

	goalsTable: ['dateRange', 'salespeople', 'teams', 'companies'],

	/**
	 * PD-16801 — the Visit Log. Salespeople and Teams pick whose visits
	 * (the rep who logged them); the rest narrow by what was recorded.
	 */
	visitLog: [
		'dateRange',
		'salespeople',
		'teams',
		'visitStatuses',
		'visitTypes',
		'visitOutcomes',
		'visitQuestions',
	],

	accountsReceivableAging: [
		'customers',
		'salespeople',
		'teams',
		'businessTypes',
		'groups',
		'companies',
	],

	taxSummary: ['dateRange', 'customers', 'companies'],

	cashReceipts: [
		'dateRange',
		'customers',
		'salespeople',
		'teams',
		'companies',
		'transactionStatus',
		'transactionPaymentMethod',
		'transactionCardType',
	],

	/**
	 * PD-14968 — declared here rather than inline on the page so the sidebar,
	 * the mobile tray and the dashboard widget picker cannot drift apart.
	 * `orderStatus` narrows to the states being chased (open orders that are
	 * already billed, say) instead of reading a whole period.
	 */
	orderReconciliation: [
		'orderKeywords',
		'customers',
		'salespeople',
		'teams',
		'companies',
		'orderStatus',
		// PD-15127 — chase one class of problem at a time; rows leave the view
		// as they are put right, so it reads as a worklist.
		'reconciliationFlags',
	],

	grossProfitDetail: [
		'dateRange',
		'customers',
		'salespeople',
		'teams',
		'businessTypes',
		'groups',
		'items',
		'productCategories',
		'companies',
	],

	creditManagement: [
		'customers',
		'salespeople',
		'teams',
		'businessTypes',
		'groups',
		'companies',
	],

	quoteItemDemandSummary: [
		'dateRange',
		'customers',
		'salespeople',
		'teams',
		'businessTypes',
		'groups',
		'companies',
	],

	quoteDemandByCustomer: [
		'dateRange',
		'customers',
		'salespeople',
		'teams',
		'businessTypes',
		'groups',
		'companies',
	],

	// No dateRange — saved prices are a current-state concept, not period-based.
	customerSavedPrices: [
		'customers',
		'salespeople',
		'teams',
		'businessTypes',
		'groups',
		'companies',
	],

	promoRedemptions: ['dateRange', 'customers', 'companies'],
	// PD-13446 — the price's logged date, competitor and customer; Salespeople
	// and Teams are the customer's rep, as the report data scope reads them.
	competitivePricing: [
		'dateRange',
		'competitors',
		'customers',
		'salespeople',
		'teams',
		'companies',
	],
	// PD-17103
	winLoss: ['dateRange', 'salespeople', 'customers', 'businessTypes', 'companies'],
	// PD-16802 — the salesperson is the rep on the quote or order itself.
	quoteOrderActivity: ['dateRange', 'salespeople', 'customers', 'companies'],
	// PD-17136 (job status is picked in the report itself)
	salesByJob: ['dateRange', 'salespeople', 'customers', 'companies'],
	openOrdersByJob: ['dateRange', 'salespeople', 'customers', 'companies'],
} as const;
